import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { API } from "../config/api";
import { format, parse } from "date-fns";
import { ro } from "date-fns/locale";
import { 
  Mail, 
  Cake, 
  TreePine,
  Sparkles,
  Loader2,
  Send,
  CheckCircle,
  Settings,
  History,
  Trash2,
  Save,
  TestTube,
  Eye,
  X,
  Upload,
  Image as ImageIcon,
  Receipt,
  FileText,
  Calendar
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Switch } from "../components/ui/switch";


const EMAIL_TYPES = {
  birthday: { label: "Zile de Naștere", icon: Cake, emoji: "🎂", color: "bg-pink-500" },
  craciun: { label: "Crăciun", icon: TreePine, emoji: "🎄", color: "bg-green-600" },
  anul_nou: { label: "Anul Nou", icon: Sparkles, emoji: "🎆", color: "bg-blue-600" },
  paste: { label: "Paște", icon: Sparkles, emoji: "🐣", color: "bg-yellow-500" },
  efactura: { label: "E-Factura ANAF", icon: Receipt, emoji: "📄", color: "bg-purple-600" }
};

export default function AutomatedEmailsPage() {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null);
  const [testingSending, setTestingSending] = useState(null);
  const [toggling, setToggling] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [logFilter, setLogFilter] = useState("all");
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ subject: "", content: "", image_url: "" });
  const [selectedLogs, setSelectedLogs] = useState([]);
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [previewEmail, setPreviewEmail] = useState(null);
  const [deletingLog, setDeletingLog] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [upcomingBirthdays, setUpcomingBirthdays] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  
  // E-Factura recipient selection
  const [efacturaDialogOpen, setEfacturaDialogOpen] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [sendingEfactura, setSendingEfactura] = useState(false);
  
  // E-Factura template editing
  const [efacturaTemplateDialog, setEfacturaTemplateDialog] = useState({ open: false, type: null });
  const [efacturaTemplateForm, setEfacturaTemplateForm] = useState({ subject: "", content: "" });
  const [savingEfacturaTemplate, setSavingEfacturaTemplate] = useState(false);
  
  // E-Factura auto recipients config
  const [efacturaRecipientsConfigOpen, setEfacturaRecipientsConfigOpen] = useState(false);
  const [efacturaRecipients, setEfacturaRecipients] = useState([]);
  const [savingEfacturaRecipients, setSavingEfacturaRecipients] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [templatesRes, logsRes, eventsRes, teamRes, efacturaRecipientsRes] = await Promise.all([
        axios.get(`${API}/email-templates`, getAuthHeader()),
        axios.get(`${API}/email-logs?limit=100`, getAuthHeader()),
        axios.get(`${API}/automated-emails/upcoming-events`, getAuthHeader()),
        axios.get(`${API}/users`, getAuthHeader()),
        axios.get(`${API}/email-templates/efactura/recipients`, getAuthHeader()).catch(() => ({ data: { recipients: [] } }))
      ]);
      setTemplates(templatesRes.data);
      setEmailLogs(logsRes.data);
      if (eventsRes.data?.upcoming_birthdays) {
        setUpcomingBirthdays(eventsRes.data.upcoming_birthdays);
      }
      // Set team members including admin
      const members = teamRes.data || [];
      setTeamMembers(members);
      // Set efactura recipients
      if (efacturaRecipientsRes.data?.recipients) {
        setEfacturaRecipients(efacturaRecipientsRes.data.recipients);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSendBirthdayEmails = async () => {
    setSending("birthday");
    try {
      const response = await axios.post(`${API}/automated-emails/check-birthdays`, {}, getAuthHeader());
      toast.success(`${response.data.sent} email(uri) trimise pentru zile de naștere`);
      fetchData();
    } catch (error) {
      toast.error("Eroare la trimiterea email-urilor");
    } finally {
      setSending(null);
    }
  };

  const handleSendHolidayEmails = async (holidayType) => {
    setSending(holidayType);
    try {
      const response = await axios.post(`${API}/automated-emails/send-holiday/${holidayType}`, {}, getAuthHeader());
      if (response.data.disabled) {
        toast.info(response.data.message);
      } else {
        toast.success(response.data.message || `${response.data.sent} email(uri) în curs de trimitere`);
      }
      fetchData();
    } catch (error) {
      toast.error("Eroare la trimiterea email-urilor");
    } finally {
      setSending(null);
    }
  };

  const openTemplateDialog = (type) => {
    const template = templates.find(t => t.type === type);
    setSelectedTemplate(type);
    setTemplateForm({
      subject: template?.subject || "",
      content: template?.content || "",
      image_url: template?.image_url || ""
    });
    setTemplateDialogOpen(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.subject || !templateForm.content) {
      toast.error("Completați subiectul și conținutul");
      return;
    }
    
    setSavingTemplate(true);
    try {
      await axios.put(`${API}/email-templates/${selectedTemplate}`, templateForm, getAuthHeader());
      toast.success("Template salvat cu succes");
      setTemplateDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Eroare la salvarea template-ului");
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Tip de fișier invalid. Permise: JPG, PNG, GIF, WEBP");
      return;
    }
    
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Fișierul este prea mare. Maxim 5MB.");
      return;
    }
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await axios.post(
        `${API}/email-templates/${selectedTemplate}/upload-image`,
        formData,
        {
          ...getAuthHeader(),
          headers: {
            ...getAuthHeader().headers,
            "Content-Type": "multipart/form-data"
          }
        }
      );
      
      setTemplateForm(prev => ({ ...prev, image_url: response.data.image_url }));
      toast.success("Imagine încărcată cu succes");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la încărcarea imaginii");
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDeleteImage = async () => {
    if (!window.confirm("Sigur doriți să ștergeți imaginea?")) return;
    
    try {
      await axios.delete(`${API}/email-templates/${selectedTemplate}/image`, getAuthHeader());
      setTemplateForm(prev => ({ ...prev, image_url: "" }));
      toast.success("Imagine ștearsă");
      fetchData();
    } catch (error) {
      toast.error("Eroare la ștergerea imaginii");
    }
  };

  const openEfacturaDialog = () => {
    // Pre-select all recipients by default
    const allEmails = teamMembers.map(m => m.email).filter(Boolean);
    setSelectedRecipients(allEmails);
    setEfacturaDialogOpen(true);
  };

  const handleToggleRecipient = (email) => {
    setSelectedRecipients(prev => 
      prev.includes(email) 
        ? prev.filter(e => e !== email)
        : [...prev, email]
    );
  };

  const handleSelectAllRecipients = () => {
    const allEmails = teamMembers.map(m => m.email).filter(Boolean);
    if (selectedRecipients.length === allEmails.length) {
      setSelectedRecipients([]);
    } else {
      setSelectedRecipients(allEmails);
    }
  };

  const handleSendEfacturaToSelected = async () => {
    if (selectedRecipients.length === 0) {
      toast.error("Selectează cel puțin un destinatar");
      return;
    }
    
    setSendingEfactura(true);
    try {
      const response = await axios.post(
        `${API}/email-templates/efactura/send-manual`,
        { recipients: selectedRecipients },
        getAuthHeader()
      );
      toast.success(`Email-uri trimise către ${response.data.sent_count} destinatari`);
      setEfacturaDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Eroare la trimiterea email-urilor");
    } finally {
      setSendingEfactura(false);
    }
  };

  const handleToggleTemplate = async (type) => {
    setToggling(type);
    try {
      const response = await axios.put(`${API}/email-templates/${type}/toggle`, {}, getAuthHeader());
      // Update the specific template
      setTemplates(prev => {
        const updated = [...prev];
        const idx = updated.findIndex(t => t.type === type);
        if (idx >= 0) {
          updated[idx] = { ...updated[idx], enabled: response.data.enabled };
        } else {
          // Add new template if it doesn't exist
          updated.push({ type, enabled: response.data.enabled });
        }
        return updated;
      });
      toast.success(response.data.enabled ? "Email-uri activate" : "Email-uri dezactivate");
      // Refresh data to get synced state
      fetchData();
    } catch (error) {
      toast.error("Eroare la schimbarea stării");
    } finally {
      setToggling(null);
    }
  };

  const openEfacturaTemplateDialog = (templateType) => {
    const template = templates.find(t => t.type === templateType);
    const defaults = {
      efactura_10: {
        subject: "Reamintire: Descărcare facturi e-Factura ANAF",
        content: "<p>Bună ziua, {nume}!</p>\n<p>Vă reamintim că este momentul să descărcați facturile din portalul <strong>e-Factura ANAF</strong> pentru luna curentă.</p>\n<p><a href=\"https://www.anaf.ro/anaf/internet/ANAF/servicii_online/e_factura\" style=\"display: inline-block; background-color: #7c3aed; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px;\">Accesează e-Factura ANAF</a></p>"
      },
      efactura_15: {
        subject: "⚠️ URGENT: Descărcare facturi e-Factura ANAF",
        content: "<p>Bună ziua, {nume}!</p>\n<div style=\"background-color: #fef3c7; border-left: 4px solid #f59e0b; padding: 15px; margin: 15px 0; border-radius: 4px;\"><p style=\"margin: 0; color: #92400e; font-weight: bold;\">⚠️ Aceasta este a doua și ultima reamintire!</p></div>\n<p>Vă rugăm să descărcați <strong>urgent</strong> facturile din portalul e-Factura ANAF.</p>\n<p><a href=\"https://www.anaf.ro/anaf/internet/ANAF/servicii_online/e_factura\" style=\"display: inline-block; background-color: #dc2626; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px;\">Accesează e-Factura ANAF ACUM</a></p>"
      }
    };
    
    setEfacturaTemplateForm({
      subject: template?.subject || defaults[templateType]?.subject || "",
      content: template?.content || defaults[templateType]?.content || ""
    });
    setEfacturaTemplateDialog({ open: true, type: templateType });
  };

  const handleSaveEfacturaTemplate = async () => {
    if (!efacturaTemplateForm.subject || !efacturaTemplateForm.content) {
      toast.error("Completați subiectul și conținutul");
      return;
    }
    
    setSavingEfacturaTemplate(true);
    try {
      await axios.put(
        `${API}/email-templates/${efacturaTemplateDialog.type}`,
        efacturaTemplateForm,
        getAuthHeader()
      );
      toast.success("Template salvat cu succes");
      setEfacturaTemplateDialog({ open: false, type: null });
      fetchData();
    } catch (error) {
      toast.error("Eroare la salvarea template-ului");
    } finally {
      setSavingEfacturaTemplate(false);
    }
  };

  // E-Factura Recipients Config Functions
  const openEfacturaRecipientsConfig = () => {
    // Pre-select saved recipients or all if none saved
    if (efacturaRecipients.length > 0) {
      setSelectedRecipients([...efacturaRecipients]);
    } else {
      const allEmails = teamMembers.map(m => m.email).filter(Boolean);
      setSelectedRecipients(allEmails);
    }
    setEfacturaRecipientsConfigOpen(true);
  };

  const handleSaveEfacturaRecipients = async () => {
    if (selectedRecipients.length === 0) {
      toast.error("Selectează cel puțin un destinatar");
      return;
    }
    
    setSavingEfacturaRecipients(true);
    try {
      await axios.put(
        `${API}/email-templates/efactura/recipients`,
        { recipients: selectedRecipients },
        getAuthHeader()
      );
      setEfacturaRecipients([...selectedRecipients]);
      toast.success(`${selectedRecipients.length} destinatar(i) salvat(i) pentru notificările automate`);
      setEfacturaRecipientsConfigOpen(false);
    } catch (error) {
      toast.error("Eroare la salvarea destinatarilor");
    } finally {
      setSavingEfacturaRecipients(false);
    }
  };

  const handleSendTestEmail = async (type) => {
    setTestingSending(type);
    try {
      await axios.post(`${API}/email-templates/${type}/test`, {}, getAuthHeader());
      toast.success("Email de test trimis la office@codrean-law.ro");
    } catch (error) {
      toast.error("Eroare la trimiterea email-ului de test");
    } finally {
      setTestingSending(null);
    }
  };

  const handleClearLogs = async () => {
    if (!window.confirm("Sigur doriți să ștergeți tot istoricul?")) return;
    
    try {
      const response = await axios.delete(`${API}/email-logs`, getAuthHeader());
      toast.success(response.data.message || "Istoric șters");
      setEmailLogs([]);
      setSelectedLogs([]);
    } catch (error) {
      toast.error("Eroare la ștergerea istoricului");
    }
  };

  const handleDeleteLog = async (logId) => {
    setDeletingLog(logId);
    try {
      await axios.delete(`${API}/email-logs/${logId}`, getAuthHeader());
      setEmailLogs(prev => prev.filter(log => log.id !== logId));
      setSelectedLogs(prev => prev.filter(id => id !== logId));
      toast.success("Înregistrare ștearsă");
    } catch (error) {
      toast.error("Eroare la ștergere");
    } finally {
      setDeletingLog(null);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedLogs.length === 0) return;
    if (!window.confirm(`Sigur doriți să ștergeți ${selectedLogs.length} înregistrări?`)) return;
    
    try {
      const response = await axios.post(`${API}/email-logs/bulk-delete`, { ids: selectedLogs }, getAuthHeader());
      setEmailLogs(prev => prev.filter(log => !selectedLogs.includes(log.id)));
      setSelectedLogs([]);
      toast.success(response.data.message || "Înregistrări șterse");
    } catch (error) {
      toast.error("Eroare la ștergere");
    }
  };

  const handleViewEmail = async (log) => {
    try {
      const response = await axios.get(`${API}/email-logs/${log.id}`, getAuthHeader());
      setPreviewEmail(response.data);
      setEmailPreviewOpen(true);
    } catch (error) {
      // If no HTML content stored, show basic info
      setPreviewEmail(log);
      setEmailPreviewOpen(true);
    }
  };

  const toggleLogSelection = (logId) => {
    setSelectedLogs(prev => 
      prev.includes(logId) 
        ? prev.filter(id => id !== logId)
        : [...prev, logId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedLogs.length === filteredLogs.length) {
      setSelectedLogs([]);
    } else {
      setSelectedLogs(filteredLogs.map(log => log.id));
    }
  };

  const filteredLogs = emailLogs.filter(log => {
    if (logFilter === "all") return true;
    if (logFilter === "birthday") return log.type === "birthday";
    if (logFilter === "efactura") return log.type?.startsWith("efactura");
    if (logFilter === "test") return log.type?.startsWith("test_");
    return log.type === `holiday_${logFilter}`;
  });

  const getTypeLabel = (type) => {
    if (type === "birthday") return "🎂 Zi naștere";
    if (type === "holiday_craciun") return "🎄 Crăciun";
    if (type === "holiday_anul_nou") return "🎆 Anul Nou";
    if (type === "holiday_paste") return "🐣 Paște";
    if (type === "efactura_10") return "📄 E-Factura (10)";
    if (type === "efactura_15") return "📄 E-Factura (15)";
    if (type === "efactura") return "📄 E-Factura";
    if (type?.startsWith("efactura")) return "📄 E-Factura";
    if (type?.startsWith("test_")) return "🧪 Test";
    return type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="automated-emails-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
          <Mail className="w-8 h-8 text-primary" />
          Email-uri Automate
        </h1>
        <p className="font-body text-muted-foreground mt-1">
          Configurați și trimiteți email-uri pentru zile de naștere și sărbători
        </p>
      </div>

      <Tabs defaultValue="send" className="space-y-6">
        <TabsList>
          <TabsTrigger value="send" className="flex items-center gap-2">
            <Send className="w-4 h-4" />
            Trimitere
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Template-uri
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="w-4 h-4" />
            Istoric ({emailLogs.length})
          </TabsTrigger>
        </TabsList>

        {/* Send Tab */}
        <TabsContent value="send" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Birthday Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Cake className="w-5 h-5 text-pink-500" />
                    Zile de Naștere
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {templates.find(t => t.type === "birthday")?.enabled !== false ? "Activ" : "Dezactivat"}
                    </span>
                    <Switch
                      checked={templates.find(t => t.type === "birthday")?.enabled !== false}
                      onCheckedChange={() => handleToggleTemplate("birthday")}
                      disabled={toggling === "birthday"}
                      data-testid="toggle-birthday"
                    />
                  </div>
                </div>
                <CardDescription>
                  Se trimit automat zilnic la ora 10:00
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 bg-secondary rounded-lg">
                  <p className="font-body text-sm">
                    <span className="font-medium">Verificare automată:</span> Zilnic la 10:00
                  </p>
                  <p className="font-body text-xs text-muted-foreground mt-1">
                    Se verifică ziua și luna nașterii (nu anul)
                  </p>
                </div>
                
                {upcomingBirthdays.length > 0 && (
                  <div className="space-y-2">
                    <p className="font-body text-sm font-medium">Zile de naștere apropiate:</p>
                    {upcomingBirthdays.slice(0, 5).map((client, idx) => (
                      <div key={idx} className="text-sm flex items-center gap-2">
                        <span className="text-lg">🎂</span>
                        <span>{client.name}</span>
                        <Badge variant="outline" className="text-xs">
                          {format(parse(client.date, "yyyy-MM-dd", new Date()), "dd/MM/yyyy")}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="flex gap-2">
                  <Button
                    onClick={handleSendBirthdayEmails}
                    disabled={sending === "birthday" || templates.find(t => t.type === "birthday")?.enabled === false}
                    className="flex-1"
                  >
                    {sending === "birthday" ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Trimite Acum
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleSendTestEmail("birthday")}
                    disabled={testingSending === "birthday"}
                    title="Trimite email de test"
                    data-testid="test-birthday"
                  >
                    {testingSending === "birthday" ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <TestTube className="w-4 h-4" />
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => openTemplateDialog("birthday")}
                  >
                    <Settings className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Holiday Cards */}
            {["craciun", "anul_nou", "paste"].map((holiday) => {
              const config = EMAIL_TYPES[holiday];
              const template = templates.find(t => t.type === holiday);
              const isEnabled = template?.enabled !== false;
              return (
                <Card key={holiday}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="font-heading flex items-center gap-2">
                        <span className="text-xl">{config.emoji}</span>
                        {config.label}
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          {isEnabled ? "Activ" : "Dezactivat"}
                        </span>
                        <Switch
                          checked={isEnabled}
                          onCheckedChange={() => handleToggleTemplate(holiday)}
                          disabled={toggling === holiday}
                          data-testid={`toggle-${holiday}`}
                        />
                      </div>
                    </div>
                    <CardDescription>
                      Trimitere manuală către toți clienții
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleSendHolidayEmails(holiday)}
                        disabled={sending === holiday || !isEnabled}
                        className="flex-1"
                      >
                        {sending === holiday ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        Trimite Email-uri
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleSendTestEmail(holiday)}
                        disabled={testingSending === holiday}
                        title="Trimite email de test"
                        data-testid={`test-${holiday}`}
                      >
                        {testingSending === holiday ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <TestTube className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => openTemplateDialog(holiday)}
                      >
                        <Settings className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {/* E-Factura Card - Redesigned with 2 templates */}
            <Card className="md:col-span-2 border-purple-200 bg-purple-50/30">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Receipt className="w-5 h-5 text-purple-600" />
                    <span className="text-xl">📄</span>
                    E-Factura ANAF
                  </CardTitle>
                </div>
                <CardDescription>
                  Reamintire automată pentru descărcarea facturilor din portalul ANAF
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Template pentru data de 10 */}
                <div className="p-4 bg-white rounded-lg border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <span className="font-bold text-purple-600">10</span>
                      </div>
                      <div>
                        <p className="font-medium">Prima notificare</p>
                        <p className="text-xs text-muted-foreground">Se trimite pe data de 10 a fiecărei luni</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-2 mr-2">
                        <span className="text-xs text-muted-foreground">
                          {templates.find(t => t.type === "efactura_10")?.enabled !== false ? "Activ" : "Dezactivat"}
                        </span>
                        <Switch
                          checked={templates.find(t => t.type === "efactura_10")?.enabled !== false}
                          onCheckedChange={() => handleToggleTemplate("efactura_10")}
                          disabled={toggling === "efactura_10"}
                          data-testid="toggle-efactura-10"
                        />
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEfacturaTemplateDialog('efactura_10')}
                      >
                        <Settings className="w-4 h-4 mr-1" />
                        Template
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSendTestEmail("efactura_10")}
                        disabled={testingSending === "efactura_10"}
                        title="Trimite email de test"
                      >
                        {testingSending === "efactura_10" ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <TestTube className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Template pentru data de 15 */}
                <div className="p-4 bg-white rounded-lg border border-orange-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                        <span className="font-bold text-orange-600">15</span>
                      </div>
                      <div>
                        <p className="font-medium">Reamintire urgentă</p>
                        <p className="text-xs text-muted-foreground">Se trimite pe data de 15 a fiecărei luni</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-2 mr-2">
                        <span className="text-xs text-muted-foreground">
                          {templates.find(t => t.type === "efactura_15")?.enabled !== false ? "Activ" : "Dezactivat"}
                        </span>
                        <Switch
                          checked={templates.find(t => t.type === "efactura_15")?.enabled !== false}
                          onCheckedChange={() => handleToggleTemplate("efactura_15")}
                          disabled={toggling === "efactura_15"}
                          data-testid="toggle-efactura-15"
                        />
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEfacturaTemplateDialog('efactura_15')}
                      >
                        <Settings className="w-4 h-4 mr-1" />
                        Template
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSendTestEmail("efactura_15")}
                        disabled={testingSending === "efactura_15"}
                        title="Trimite email de test"
                      >
                        {testingSending === "efactura_15" ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <TestTube className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Destinatari și Acțiuni */}
                <div 
                  className="p-3 bg-purple-50 rounded-lg border border-purple-200 cursor-pointer hover:bg-purple-100 transition-colors"
                  onClick={openEfacturaRecipientsConfig}
                  data-testid="efactura-recipients-config"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-purple-800 font-medium flex items-center gap-2">
                        <Settings className="w-4 h-4" />
                        Destinatari pentru notificări automate
                      </p>
                      <p className="text-xs text-purple-600 mt-1">
                        {efacturaRecipients.length > 0 
                          ? `${efacturaRecipients.length} destinatar(i) selectat(i)`
                          : "Click pentru a configura destinatarii"
                        }
                      </p>
                    </div>
                    <Badge variant="secondary" className="bg-purple-200 text-purple-800">
                      Configurează
                    </Badge>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    onClick={openEfacturaDialog}
                    variant="outline"
                    className="flex-1"
                    data-testid="send-efactura-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Trimite Manual Acum
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.entries(EMAIL_TYPES).map(([type, config]) => {
              const template = templates.find(t => t.type === type);
              return (
                <Card key={type}>
                  <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2 text-lg">
                      <span>{config.emoji}</span>
                      {config.label}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">Subiect:</Label>
                      <p className="font-body text-sm">{template?.subject || "Template implicit"}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Conținut:</Label>
                      <p className="font-body text-sm text-muted-foreground line-clamp-3">
                        {template?.content || "Conținut implicit"}
                      </p>
                    </div>
                    {template?.image_url && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Imagine atașată:</Label>
                        <div className="mt-1 border rounded overflow-hidden">
                          <img 
                            src={template.image_url} 
                            alt="Felicitare" 
                            className="w-full h-24 object-cover"
                          />
                        </div>
                      </div>
                    )}
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => openTemplateDialog(type)}
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Configurează Template
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="font-heading flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Istoric Email-uri Trimise
                </CardTitle>
                <div className="flex gap-2">
                  {selectedLogs.length > 0 && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleBulkDelete}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Șterge ({selectedLogs.length})
                    </Button>
                  )}
                  {emailLogs.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleClearLogs}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Șterge Tot
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Filters */}
              <div className="flex gap-2 mt-4 flex-wrap">
                {[
                  { value: "all", label: "Toate" },
                  { value: "birthday", label: "🎂 Zile naștere" },
                  { value: "craciun", label: "🎄 Crăciun" },
                  { value: "anul_nou", label: "🎆 Anul Nou" },
                  { value: "paste", label: "🐣 Paște" },
                  { value: "efactura", label: "📄 E-Factura" },
                  { value: "test", label: "🧪 Test" },
                ].map((filter) => (
                  <Button
                    key={filter.value}
                    size="sm"
                    variant={logFilter === filter.value ? "default" : "outline"}
                    onClick={() => setLogFilter(filter.value)}
                  >
                    {filter.label}
                  </Button>
                ))}
              </div>
            </CardHeader>
            <CardContent>
              {filteredLogs.length === 0 ? (
                <div className="text-center py-8">
                  <Mail className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="font-body text-muted-foreground">
                    Nu există email-uri în istoric
                    {logFilter !== "all" && " pentru filtrul selectat"}
                  </p>
                </div>
              ) : (
                <>
                  {/* Select All */}
                  <div className="flex items-center gap-2 mb-3 pb-3 border-b">
                    <Checkbox
                      checked={selectedLogs.length === filteredLogs.length && filteredLogs.length > 0}
                      onCheckedChange={toggleSelectAll}
                      id="select-all"
                    />
                    <Label htmlFor="select-all" className="text-sm text-muted-foreground cursor-pointer">
                      Selectează toate ({filteredLogs.length})
                    </Label>
                  </div>
                  
                  <ScrollArea className="h-[400px]">
                    <div className="space-y-2">
                      {filteredLogs.map((log) => (
                        <div
                          key={log.id}
                          className="p-3 rounded-lg border bg-card flex items-center justify-between hover:bg-accent/50 cursor-pointer transition-colors"
                          onClick={() => handleViewEmail(log)}
                        >
                          <div className="flex items-center gap-3">
                            <Checkbox
                              checked={selectedLogs.includes(log.id)}
                              onCheckedChange={() => toggleLogSelection(log.id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <CheckCircle className="w-4 h-4 text-success" />
                            <div>
                              <p className="font-body text-sm font-medium">{log.client_name}</p>
                              <p className="font-mono text-xs text-muted-foreground">{log.email}</p>
                              {log.subject && (
                                <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{log.subject}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-right">
                              <Badge variant="secondary" className="mb-1">
                                {getTypeLabel(log.type)}
                              </Badge>
                              <p className="font-mono text-xs text-muted-foreground">
                                {format(new Date(log.sent_at), "dd/MM/yyyy HH:mm")}
                              </p>
                            </div>
                            <div className="flex flex-col gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewEmail(log);
                                }}
                                title="Vezi email"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-destructive hover:text-destructive"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteLog(log.id);
                                }}
                                disabled={deletingLog === log.id}
                                title="Șterge"
                              >
                                {deletingLog === log.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Email Preview Dialog */}
      <Dialog open={emailPreviewOpen} onOpenChange={setEmailPreviewOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Mail className="w-5 h-5" />
              {previewEmail?.subject || "Email Trimis"}
            </DialogTitle>
            <DialogDescription>
              Către: {previewEmail?.email} • {previewEmail?.sent_at && format(new Date(previewEmail.sent_at), "dd/MM/yyyy HH:mm")}
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="h-[50vh]">
            {previewEmail?.html_content ? (
              <div 
                className="border rounded-lg overflow-hidden"
                dangerouslySetInnerHTML={{ __html: previewEmail.html_content }}
              />
            ) : previewEmail?.content ? (
              <div className="p-4 bg-muted rounded-lg">
                <p className="whitespace-pre-wrap">{previewEmail.content}</p>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>Conținutul email-ului nu este disponibil.</p>
                <p className="text-sm mt-2">Email-urile mai vechi nu au fost salvate cu conținut complet.</p>
              </div>
            )}
          </ScrollArea>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailPreviewOpen(false)}>
              <X className="w-4 h-4 mr-2" />
              Închide
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Edit Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Configurare Template - {selectedTemplate && EMAIL_TYPES[selectedTemplate]?.label}
            </DialogTitle>
            <DialogDescription>
              Configurați mesajul care va fi trimis. Folosiți {"{nume}"} pentru a include numele clientului.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="subject">Subiect Email</Label>
              <Input
                id="subject"
                value={templateForm.subject}
                onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                placeholder="Ex: 🎂 La Mulți Ani!"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="content">Conținut Mesaj</Label>
              <Textarea
                id="content"
                value={templateForm.content}
                onChange={(e) => setTemplateForm({ ...templateForm, content: e.target.value })}
                rows={6}
                placeholder="Scrieți mesajul aici... Folosiți {nume} pentru a include numele clientului."
              />
              <p className="text-xs text-muted-foreground">
                Exemplu: "Dragă {"{nume}"}, îți urăm..."
              </p>
            </div>
            
            {/* Image Upload Section */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <ImageIcon className="w-4 h-4" />
                Imagine / Felicitare (opțional)
              </Label>
              
              {templateForm.image_url ? (
                <div className="space-y-2">
                  <div className="relative border rounded-lg overflow-hidden">
                    <img 
                      src={templateForm.image_url} 
                      alt="Felicitare" 
                      className="w-full max-h-48 object-contain bg-gray-50"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingImage}
                    >
                      {uploadingImage ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      Schimbă Imaginea
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={handleDeleteImage}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Șterge
                    </Button>
                  </div>
                </div>
              ) : (
                <div 
                  className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploadingImage ? (
                    <Loader2 className="w-8 h-8 mx-auto text-muted-foreground animate-spin" />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">
                        Click pentru a încărca o imagine
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        JPG, PNG, GIF, WEBP (max 5MB)
                      </p>
                    </>
                  )}
                </div>
              )}
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleImageUpload}
                className="hidden"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>
              Anulează
            </Button>
            <Button onClick={handleSaveTemplate} disabled={savingTemplate}>
              {savingTemplate ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Salvează Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* E-Factura Recipients Dialog */}
      <Dialog open={efacturaDialogOpen} onOpenChange={setEfacturaDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="efactura-recipients-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Receipt className="w-5 h-5 text-purple-600" />
              Selectează Destinatari E-Factura
            </DialogTitle>
            <DialogDescription>
              Alege persoanele care vor primi email-ul de reamintire e-Factura
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {/* Select All */}
            <div 
              className="flex items-center gap-3 p-3 rounded-lg border mb-3 cursor-pointer hover:bg-secondary/50 transition-colors"
              onClick={handleSelectAllRecipients}
            >
              <Checkbox 
                checked={teamMembers.filter(m => m.email).length > 0 && 
                         selectedRecipients.length === teamMembers.filter(m => m.email).length}
                onCheckedChange={handleSelectAllRecipients}
              />
              <div>
                <p className="font-medium text-sm">Selectează toți</p>
                <p className="text-xs text-muted-foreground">
                  {teamMembers.filter(m => m.email).length} persoane disponibile
                </p>
              </div>
            </div>
            
            <ScrollArea className="h-[250px] pr-2">
              <div className="space-y-2">
                {teamMembers.filter(m => m.email).map((member) => (
                  <div
                    key={member.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedRecipients.includes(member.email) 
                        ? "bg-purple-50 border-purple-200" 
                        : "hover:bg-secondary/50"
                    }`}
                    onClick={() => handleToggleRecipient(member.email)}
                  >
                    <Checkbox 
                      checked={selectedRecipients.includes(member.email)}
                      onCheckedChange={() => handleToggleRecipient(member.email)}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{member.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{member.email}</p>
                    </div>
                    {member.role === "admin" && (
                      <Badge variant="secondary" className="text-xs">Admin</Badge>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
            
            {selectedRecipients.length > 0 && (
              <p className="text-sm text-muted-foreground mt-3 pt-3 border-t">
                <strong>{selectedRecipients.length}</strong> destinatar(i) selectat(i)
              </p>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEfacturaDialogOpen(false)}>
              Anulează
            </Button>
            <Button 
              onClick={handleSendEfacturaToSelected} 
              disabled={sendingEfactura || selectedRecipients.length === 0}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {sendingEfactura ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Trimite Email ({selectedRecipients.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* E-Factura Template Edit Dialog */}
      <Dialog open={efacturaTemplateDialog.open} onOpenChange={(open) => setEfacturaTemplateDialog({ open, type: open ? efacturaTemplateDialog.type : null })}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Settings className="w-5 h-5" />
              {efacturaTemplateDialog.type === 'efactura_10' ? 'Template Ziua 10' : 'Template Ziua 15'}
            </DialogTitle>
            <DialogDescription>
              Configurați mesajul pentru {efacturaTemplateDialog.type === 'efactura_10' ? 'prima notificare (ziua 10)' : 'reamintirea urgentă (ziua 15)'}. 
              Folosiți {"{nume}"} pentru a include numele destinatarului.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="efactura-subject">Subiect Email</Label>
              <Input
                id="efactura-subject"
                value={efacturaTemplateForm.subject}
                onChange={(e) => setEfacturaTemplateForm({ ...efacturaTemplateForm, subject: e.target.value })}
                placeholder={efacturaTemplateDialog.type === 'efactura_10' ? "Reamintire: Descărcare facturi e-Factura ANAF" : "⚠️ URGENT: Descărcare facturi e-Factura ANAF"}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="efactura-content">Conținut Mesaj (HTML)</Label>
              <Textarea
                id="efactura-content"
                value={efacturaTemplateForm.content}
                onChange={(e) => setEfacturaTemplateForm({ ...efacturaTemplateForm, content: e.target.value })}
                rows={10}
                placeholder="Scrieți mesajul aici... Folosiți {nume} pentru a include numele destinatarului."
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Variabile disponibile: {"{nume}"} - numele destinatarului, {"{luna}"} - luna curentă
              </p>
            </div>

            {/* Preview */}
            <div className="space-y-2">
              <Label>Previzualizare</Label>
              <div 
                className="p-4 border rounded-lg bg-white max-h-48 overflow-y-auto"
                dangerouslySetInnerHTML={{ 
                  __html: efacturaTemplateForm.content
                    .replace(/{nume}/g, 'Exemplu Nume')
                    .replace(/{luna}/g, new Date().toLocaleString('ro-RO', { month: 'long', year: 'numeric' }))
                }}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEfacturaTemplateDialog({ open: false, type: null })}>
              Anulează
            </Button>
            <Button onClick={handleSaveEfacturaTemplate} disabled={savingEfacturaTemplate}>
              {savingEfacturaTemplate ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Salvează Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* E-Factura Recipients Config Dialog */}
      <Dialog open={efacturaRecipientsConfigOpen} onOpenChange={setEfacturaRecipientsConfigOpen}>
        <DialogContent className="sm:max-w-md" data-testid="efactura-recipients-config-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Receipt className="w-5 h-5 text-purple-600" />
              Configurare Destinatari E-Factura
            </DialogTitle>
            <DialogDescription>
              Selectează persoanele care vor primi automat notificările e-Factura pe datele de 10 și 15 ale fiecărei luni.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {/* Select All */}
            <div 
              className="flex items-center gap-3 p-3 rounded-lg border mb-3 cursor-pointer hover:bg-secondary/50 transition-colors"
              onClick={handleSelectAllRecipients}
            >
              <Checkbox 
                checked={teamMembers.filter(m => m.email).length > 0 && 
                         selectedRecipients.length === teamMembers.filter(m => m.email).length}
                onCheckedChange={handleSelectAllRecipients}
              />
              <div>
                <p className="font-medium text-sm">Selectează toți</p>
                <p className="text-xs text-muted-foreground">
                  {teamMembers.filter(m => m.email).length} persoane disponibile
                </p>
              </div>
            </div>
            
            <ScrollArea className="h-[250px] pr-2">
              <div className="space-y-2">
                {teamMembers.filter(m => m.email).map((member) => (
                  <div
                    key={member.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedRecipients.includes(member.email) 
                        ? "bg-purple-50 border-purple-200" 
                        : "hover:bg-secondary/50"
                    }`}
                    onClick={() => handleToggleRecipient(member.email)}
                  >
                    <Checkbox 
                      checked={selectedRecipients.includes(member.email)}
                      onCheckedChange={() => handleToggleRecipient(member.email)}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{member.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{member.email}</p>
                    </div>
                    {member.role === "admin" && (
                      <Badge variant="secondary" className="text-xs">Admin</Badge>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
            
            {selectedRecipients.length > 0 && (
              <div className="mt-3 pt-3 border-t">
                <p className="text-sm text-muted-foreground">
                  <strong>{selectedRecipients.length}</strong> destinatar(i) vor primi notificările automate
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEfacturaRecipientsConfigOpen(false)}>
              Anulează
            </Button>
            <Button 
              onClick={handleSaveEfacturaRecipients} 
              disabled={savingEfacturaRecipients || selectedRecipients.length === 0}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {savingEfacturaRecipients ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Salvează Destinatari
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
